import { useCallback, useEffect, useState } from 'react';
import { Icon } from '@/components';
import type { ItemStat } from '@/types/domain';

/** Item-analysis row joined with its answer key, for the teaching reveal. */
export interface MissedQuestion extends ItemStat {
  options: string[];
  answer: number | number[];
  rationale?: string;
  imageUrl?: string;
}

const LETTERS = ['ก', 'ข', 'ค', 'ง', 'จ', 'ฉ'];
const isCorrect = (answer: number | number[], oi: number) => (Array.isArray(answer) ? answer.includes(oi) : answer === oi);

/**
 * Act 2 — click through the hardest questions one at a time. Each slide leads
 * with "X% of the room missed this" (so everyone sees they're not alone) and a
 * second click reveals the correct answer + rationale to reteach.
 *
 * Right: reveal, then advance. Left: previous slide (or back to the scoreboard).
 */
export function MissedQuestionsDeck({ items, onBack, kind }: { items: MissedQuestion[]; onBack: () => void; kind: 'pre' | 'post' }) {
  const [idx, setIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);

  const last = items.length - 1;

  const next = useCallback(() => {
    if (!revealed) setRevealed(true);
    else if (idx < last) { setIdx(idx + 1); setRevealed(false); }
  }, [revealed, idx, last]);

  const prev = useCallback(() => {
    if (revealed) setRevealed(false);
    else if (idx > 0) setIdx(idx - 1);
    else onBack();
  }, [revealed, idx, onBack]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev]);

  const q = items[idx];
  if (!q) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100%', gap: 16 }}>
        <div className="th" style={{ color: 'var(--ink-3)', fontSize: 17 }}>ยังไม่มีข้อมูลการตอบ — ดูได้หลังมีผู้ทำแบบทดสอบ</div>
        <button className="btn btn-primary" onClick={onBack}><Icon name="chevL" size={16} /> Back to scoreboard</button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 16, maxWidth: 1000, width: '100%', margin: '0 auto' }}>
      {/* progress */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 'none' }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}><Icon name="chevL" size={15} /> Scoreboard</button>
        <span className="u-mono" style={{ color: 'var(--ink-3)', fontSize: 14 }}>{idx + 1} / {items.length} · {kind === 'pre' ? 'ก่อนเรียน' : 'หลังเรียน'} · hardest first</span>
      </div>

      {/* the social-proof headline — "you're not alone" */}
      <div style={{ textAlign: 'center', flex: 'none' }}>
        <div style={{ fontSize: 44, fontWeight: 800, lineHeight: 1, color: q.wrongPct >= 50 ? 'var(--danger)' : 'var(--ochre)' }}>🙈 {q.wrongPct}%</div>
        <div className="th" style={{ fontSize: 16, color: 'var(--ink-2)', marginTop: 6 }}>ของห้องตอบผิดข้อนี้ · {q.wrong} จาก {q.total} คน</div>
      </div>

      {/* question body */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {q.imageUrl && (
          <img src={q.imageUrl} alt="" style={{ maxWidth: '100%', maxHeight: 280, display: 'block', margin: '0 auto 16px', borderRadius: 12, border: '1px solid var(--line)' }} />
        )}
        <div className="th" style={{ fontSize: 23, fontWeight: 600, lineHeight: 1.5, marginBottom: 18, textAlign: 'center' }}>{q.stem}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {q.options.map((opt, oi) => {
            const correct = revealed && isCorrect(q.answer, oi);
            return (
              <div
                key={oi}
                style={{
                  display: 'flex', gap: 12, alignItems: 'center', padding: '13px 16px', borderRadius: 12,
                  border: `2px solid ${correct ? 'var(--sage)' : 'var(--line)'}`,
                  background: correct ? 'var(--sage-tint)' : 'transparent',
                  transition: 'background .2s, border-color .2s',
                }}
              >
                <span className="u-mono" style={{ width: 22, flex: 'none', color: correct ? 'var(--sage-deep)' : 'var(--ink-3)' }}>{LETTERS[oi] ?? oi + 1}</span>
                <span className="th" style={{ flex: 1, fontSize: 18, fontWeight: correct ? 600 : 400 }}>{opt}</span>
                {correct && <Icon name="check" size={20} style={{ color: 'var(--sage-deep)', flex: 'none' }} />}
              </div>
            );
          })}
        </div>
        {revealed && q.rationale && (
          <div className="th" style={{ marginTop: 16, padding: '14px 16px', background: 'var(--card-2)', borderRadius: 12, fontSize: 16, lineHeight: 1.6, color: 'var(--ink-2)' }}>💡 {q.rationale}</div>
        )}
      </div>

      {/* nav */}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', alignItems: 'center', flex: 'none' }}>
        <button className="btn btn-ghost" onClick={prev}><Icon name="chevL" size={16} /> Back</button>
        <button className="btn btn-primary btn-lg" onClick={next}>
          {!revealed ? 'Reveal answer' : idx < last ? <>Next question <Icon name="arrowR" size={16} /></> : 'Done ✓'}
        </button>
      </div>
    </div>
  );
}
